import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/dashboard';
import { GoogleLandingPage } from '../components/search/GoogleLandingPage';
import { GoogleResultsPage } from '../components/search/GoogleResultsPage';
import { SearchQuery, SearchEntityType, SortOrder } from '../types/search';

export const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasSearched, setHasSearched] = useState(false);
  const [searchQuery, setSearchQuery] = useState<SearchQuery | undefined>();

  useEffect(() => {
    const q = searchParams.get('q');
    const entityTypes = searchParams.getAll('entityTypes');
    const organizationType = searchParams.get('organizationType');
    const location = searchParams.get('location');
    const role = searchParams.get('role');
    const department = searchParams.get('department');
    const organizationId = searchParams.get('organizationId');
    const stakeholderId = searchParams.get('stakeholderId');
    const page = searchParams.get('page');
    const limit = searchParams.get('limit');
    const sortBy = searchParams.get('sortBy');
    const sortOrder = searchParams.get('sortOrder');
    const includeHighlights = searchParams.get('includeHighlights');

    if (
      q || entityTypes.length > 0 || organizationType || location || role || department || organizationId || stakeholderId
    ) {
      const query: SearchQuery = {
        q: q || undefined,
        entityTypes: entityTypes.length > 0 ? (entityTypes as SearchEntityType[]) : Object.values(SearchEntityType),
        organizationType: organizationType || undefined,
        location: location || undefined,
        role: role || undefined,
        department: department || undefined,
        organizationId: organizationId || undefined,
        stakeholderId: stakeholderId || undefined,
        page: page ? parseInt(page) : 1,
        limit: limit ? parseInt(limit) : 20,
        sortBy: sortBy || 'relevance',
        sortOrder: (sortOrder as SortOrder) || SortOrder.DESC,
        includeHighlights: includeHighlights !== 'false',
      };
      setSearchQuery(query);
      setHasSearched(true);
    } else {
      setHasSearched(false);
      setSearchQuery(undefined);
    }
  }, [searchParams]);

  const handleSearch = (query: SearchQuery) => {
    setSearchQuery(query);
    setHasSearched(true);

    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.entityTypes && query.entityTypes.length < Object.values(SearchEntityType).length) {
      query.entityTypes.forEach((type) => params.append('entityTypes', type));
    }
    if (query.organizationType) params.set('organizationType', query.organizationType);
    if (query.location) params.set('location', query.location);
    if (query.role) params.set('role', query.role);
    if (query.department) params.set('department', query.department);
    if (query.organizationId) params.set('organizationId', query.organizationId);
    if (query.stakeholderId) params.set('stakeholderId', query.stakeholderId);
    if (query.page && query.page > 1) params.set('page', query.page.toString());
    if (query.limit && query.limit !== 20) params.set('limit', query.limit.toString());
    if (query.sortBy && query.sortBy !== 'relevance') params.set('sortBy', query.sortBy);
    if (query.sortOrder && query.sortOrder !== SortOrder.DESC) params.set('sortOrder', query.sortOrder);
    if (query.includeHighlights === false) params.set('includeHighlights', 'false');

    setSearchParams(params, { replace: true });
  };

  return (
    <DashboardLayout>
      {hasSearched ? (
        <GoogleResultsPage initialQuery={searchQuery} onSearch={handleSearch} />
      ) : (
        <GoogleLandingPage onSearch={handleSearch} />
      )}
    </DashboardLayout>
  );
};
